import React, { useState } from 'react';
import { AutoComplete, Button, Empty, Input, Modal, Space, Table, Tag, Tooltip, message } from 'antd';
import { PrinterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { batchNonStdLabelConsume, batchNonStdLabelGroup, voidNonStdLabel } from '../../services/nonstandardOps';
import { NonStdLabel, NonStdLabelGroupInfo } from '../../types/nonstandardOps';
import LabelPrintModal, { LabelModalMode } from './LabelPrintModal';
import {
  ConsumedTag, LabelGroupSections, LabelSection, consumedRowStyle, groupFilterOption, hasGroups, hasSizeInfo, normGroup, sizeWeightCell,
} from './labelGroups';

interface Props {
  instructionId: number;
  instructionCode?: string;
  labels: NonStdLabel[];
  /** 未分组标签的下一个建议编号 */
  nextNumber?: string;
  /** 本指令用过的标识组（含每组的下一个建议编号）和最近一张标签用的组 */
  groups?: NonStdLabelGroupInfo[];
  lastGroup?: string;
  /** 可以新建、修改、作废（执行阶段内）；后端还会再校验"员工只能改自己建的" */
  canEdit: boolean;
  /** 可以打印、重打（到终审完成为止） */
  canPrint: boolean;
  /** 可以标记/取消"已消耗"（指令进行中、确认完成还没提交；任何仓库员工都可以） */
  canConsume?: boolean;
  /** 显示"确认完成"阶段的核对结果（已贴标签、放置位置、未使用）：只读视图和终审页用 */
  showPlacement?: boolean;
  /** 不显示"创建""打印"两列（终审页只需要容器本身的信息） */
  hideCreatedPrinted?: boolean;
  /**
   * 紧凑布局（终审页的"明细-容器与标签"）：列宽固定、内容物和备注占用剩余宽度，过长时省略并在鼠标悬停时显示完整内容；
   * "容器核对"列改成"仓位"，只显示当前仓位（没有就是 -），操作列收窄。
   */
  compact?: boolean;
  onChanged: () => void;
}

const HINT = '把执行结果放进某个容器后，打印一张标签标记它（例如可售和不可售分开存放，可售的再按产品分开）。标签上有固定信息（指令编码、所属项目、操作人、打印时间）和可以手填的编号、内容物、备注，三项都可以留空；还可以给标签设一个“标识组”（打印在标签最上面一行，同一个组的编号单独计数），并记录容器的尺寸和重量（不打印）。可以边做边打印，也可以先保存、全部做完再批量打印；打印前会先预览。优先发送到打印代理自动找标签打印机，代理不可用时改用浏览器打印。容器里的东西被二次加工、容器已经不存在时，标记为“已消耗”（不需要再贴标签和记录位置，也不能再打印）。';
const CONSUMED_PRINT_TIP = '已消耗的标签不能打印（容器已不存在）；如果标错了，先取消“已消耗”';

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

type ModalState = { mode: LabelModalMode; labels: NonStdLabel[] } | null;

/** 单行显示，过长时用省略号，鼠标悬停显示完整内容 */
const ellipsisCell = (value?: string | null) => {
  const text = String(value || '').trim();
  if (!text) return '-';
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</span>} placement="topLeft">
      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
    </Tooltip>
  );
};

/** 容器标签清单：新建并打印、保存后稍后打印、批量打印、随时重打；按标识组分段显示，可以标记"已消耗"。 */
const LabelsPanel: React.FC<Props> = ({
  instructionId, instructionCode, labels, nextNumber, groups, lastGroup, canEdit, canPrint, canConsume, showPlacement,
  hideCreatedPrinted, compact, onChanged,
}) => {
  const [modal, setModal] = useState<ModalState>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [groupTarget, setGroupTarget] = useState<{ ids: number[]; group: string } | null>(null);
  const [groupDraft, setGroupDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const handleVoid = (label: NonStdLabel) => {
    let reason = '';
    Modal.confirm({
      title: '作废这张标签？',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>作废后不再显示，也不能再打印，但会保留在系统里以便追溯。已经打印出来的实物标签请自行处理。</div>
          <Input placeholder="作废原因（选填）" onChange={e => { reason = e.target.value; }} />
        </div>
      ),
      okText: '作废',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await voidNonStdLabel(label.id, reason);
          message.success('已作废');
          setSelected(prev => prev.filter(id => id !== label.id));
          onChanged();
        } catch (err: any) {
          message.error(errorText(err, '作废失败'));
          throw err;
        }
      },
    });
  };

  const doConsume = async (ids: number[], consumed: boolean) => {
    try {
      const result = await batchNonStdLabelConsume(instructionId, { label_ids: ids, consumed });
      if (result.labels.length === 0 && result.skipped_unused === 0) {
        message.info(consumed ? '所选标签已经都是“已消耗”了' : '所选标签都不是“已消耗”');
      } else {
        message.success(`${consumed ? '已标记为已消耗' : '已取消已消耗'}：${result.labels.length} 个${result.skipped_unused ? `（另有 ${result.skipped_unused} 个标记了“未使用”的容器已跳过）` : ''}`);
      }
      onChanged();
    } catch (err: any) {
      message.error(errorText(err, '操作失败'));
    }
  };

  /** 标记已消耗：单个直接做，批量/整组先确认一下（随时可以取消） */
  const askConsume = (ids: number[], consumed: boolean, what: string) => {
    if (!consumed) { void doConsume(ids, false); return; }
    Modal.confirm({
      title: `把${what}标记为“已消耗”？`,
      content: '“已消耗”表示容器里的东西已经被二次加工、这个容器已经不存在了：不再需要贴标签和记录位置，也不能再打印标签。标错了可以随时取消。',
      okText: '标记为已消耗',
      cancelText: '取消',
      onOk: () => doConsume(ids, true),
    });
  };

  const submitGroup = async () => {
    if (!groupTarget) return;
    setBusy(true);
    try {
      await batchNonStdLabelGroup(instructionId, { label_ids: groupTarget.ids, group: normGroup(groupDraft) });
      message.success('标识组已修改');
      setGroupTarget(null);
      onChanged();
    } catch (err: any) {
      message.error(errorText(err, '修改标识组失败'));
    } finally {
      setBusy(false);
    }
  };

  const selectedLabels = labels.filter(label => selected.includes(label.id));
  const printableSelected = selectedLabels.filter(label => !label.consumed);
  const unprinted = labels.filter(label => label.print_count === 0 && !label.consumed).length;
  const consumedTotal = labels.filter(label => label.consumed).length;
  const canConsumeRow = (label: NonStdLabel) => !label.consumed && !label.unused;
  const sizeColumn = hasSizeInfo(labels);
  const selectable = canPrint || canEdit || !!canConsume;

  const renderTable = (rows: NonStdLabel[]) => (
    <Table
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={rows}
      // 分组后每组一张表，固定列宽才能让各组的列对齐
      tableLayout={compact || hasGroups(labels) ? 'fixed' : undefined}
      onRow={row => ({ style: consumedRowStyle(row) })}
      rowSelection={selectable ? {
        // 每个分组一张表：这张表只管自己那几行，别的表里已选中的保持不变
        selectedRowKeys: selected.filter(id => rows.some(row => row.id === id)),
        onChange: keys => setSelected(prev => [...prev.filter(id => !rows.some(row => row.id === id)), ...(keys as number[])]),
      } : undefined}
      columns={[
        { title: '编号', dataIndex: 'number', width: compact ? 70 : 80, render: (v: string) => (v ? <b>{v}</b> : '-') },
        compact
          ? { title: '内容物', dataIndex: 'contents', ellipsis: { showTitle: false }, render: (v: string) => ellipsisCell(v) }
          : { title: '内容物', dataIndex: 'contents', render: (v: string) => v || '-' },
        compact
          ? { title: '备注', dataIndex: 'remark', ellipsis: { showTitle: false }, render: (v: string) => ellipsisCell(v) }
          : { title: '备注', dataIndex: 'remark', render: (v: string) => v || '-' },
        ...(sizeColumn ? [compact
          ? { title: '尺寸/重量', width: 150, ellipsis: { showTitle: false }, render: (_: any, row: NonStdLabel) => sizeWeightCell(row, { oneLine: true }) }
          : { title: '尺寸/重量', width: 170, render: (_: any, row: NonStdLabel) => sizeWeightCell(row) }] : []),
        ...(hideCreatedPrinted ? [] : [
          {
            title: '创建',
            width: 150,
            render: (_: any, row: NonStdLabel) => (
              <div style={{ fontSize: 12 }}>
                <div>{row.created_by_name || '-'}</div>
                <div style={{ color: '#8c8c8c' }}><TimezoneAwareTime time={row.created_at} format="MM-DD HH:mm" showTimezone={false} /></div>
              </div>
            ),
          },
          {
            title: '打印',
            width: 170,
            render: (_: any, row: NonStdLabel) => (row.print_count > 0 ? (
              <div style={{ fontSize: 12 }}>
                <div>已打印 {row.print_count} 张</div>
                <div style={{ color: '#8c8c8c' }}>
                  {row.last_printed_by_name || ''} {row.last_printed_at ? <TimezoneAwareTime time={row.last_printed_at} format="MM-DD HH:mm" showTimezone={false} /> : null}
                </div>
              </div>
            ) : <Tag>未打印</Tag>),
          },
        ]),
        ...(showPlacement && compact ? [{
          title: '仓位',
          dataIndex: 'location',
          width: 110,
          ellipsis: { showTitle: false },
          // 只显示当前仓位；没有（含"未使用"的容器）就是 -；已消耗的容器已不存在，显示灰色"已消耗"
          render: (_: any, row: NonStdLabel) => (row.consumed ? <ConsumedTag /> : row.unused ? '-' : ellipsisCell(row.location)),
        }] : []),
        ...(showPlacement && !compact ? [{
          title: '容器核对',
          width: 200,
          render: (_: any, row: NonStdLabel) => {
            if (row.consumed) {
              return <div style={{ fontSize: 12 }}><ConsumedTag />{row.consumed_by_name ? <div style={{ color: '#8c8c8c' }}>{row.consumed_by_name}</div> : null}</div>;
            }
            if (row.unused) {
              return <div style={{ fontSize: 12 }}><Tag>未使用</Tag><div style={{ color: '#8c8c8c' }}>{row.unused_reason}</div></div>;
            }
            if (row.placement_status === 'placed') {
              return <div style={{ fontSize: 12 }}><Tag color="green">已就位</Tag><div>{row.location}</div></div>;
            }
            return <div style={{ fontSize: 12 }}><Tag color="orange">待处理</Tag><div style={{ color: '#8c8c8c' }}>{row.labeled ? '已贴标签' : '未贴标签'}{row.location ? `，位置：${row.location}` : ''}</div></div>;
          },
        }] : []),
        // 没有"容器核对"/"仓位"列（执行页的标签清单）时，已消耗要在别处标出来
        ...(!showPlacement && consumedTotal > 0 ? [{
          title: '状态',
          width: 80,
          render: (_: any, row: NonStdLabel) => (row.consumed ? <ConsumedTag /> : '-'),
        }] : []),
        {
          title: '操作',
          width: compact ? 80 : (canEdit || canConsume ? 230 : 150),
          render: (_: any, row: NonStdLabel) => (
            <Space size={8} wrap>
              {canPrint ? (row.consumed
                ? <Tooltip title={CONSUMED_PRINT_TIP}><span style={{ color: '#bfbfbf', cursor: 'not-allowed' }}>{row.print_count > 0 ? '重打' : '打印'}</span></Tooltip>
                : <a onClick={() => setModal({ mode: 'print', labels: [row] })}>{row.print_count > 0 ? '重打' : '打印'}</a>) : null}
              {canEdit ? <a onClick={() => setModal({ mode: 'edit', labels: [row] })}>编辑</a> : null}
              {canConsume && row.consumed ? <a onClick={() => doConsume([row.id], false)}>取消已消耗</a> : null}
              {canConsume && canConsumeRow(row) ? <a onClick={() => doConsume([row.id], true)}>标记已消耗</a> : null}
              {canEdit ? <a style={{ color: '#cf1322' }} onClick={() => handleVoid(row)}>作废</a> : null}
            </Space>
          ),
        },
      ]}
    />
  );

  /** 分组标题右侧的整组操作 */
  const renderSectionActions = (section: LabelSection<NonStdLabel>) => {
    const ids = section.rows.map(row => row.id);
    const printable = section.rows.filter(row => !row.consumed);
    const consumable = section.rows.filter(canConsumeRow);
    const groupLabel = section.name ? `“${section.name}”组` : '“未分组”';
    return (
      <>
        {canPrint ? (printable.length > 0
          ? <a onClick={() => setModal({ mode: 'print', labels: printable })}>打印本组{printable.length < section.rows.length ? `（${printable.length}）` : ''}</a>
          : <Tooltip title={CONSUMED_PRINT_TIP}><span style={{ color: '#bfbfbf', cursor: 'not-allowed' }}>打印本组</span></Tooltip>) : null}
        {canConsume && consumable.length > 0 ? <a onClick={() => askConsume(consumable.map(row => row.id), true, `${groupLabel}的 ${consumable.length} 个容器`)}>整组标记已消耗</a> : null}
        {canConsume && section.consumedCount > 0 ? <a onClick={() => askConsume(section.rows.filter(row => row.consumed).map(row => row.id), false, '')}>整组取消已消耗</a> : null}
        {canEdit ? <a onClick={() => { setGroupTarget({ ids, group: section.name }); setGroupDraft(section.name); }}>修改标识组</a> : null}
      </>
    );
  };

  return (
    <div>
      <Space style={{ marginBottom: 8 }} wrap>
        {canEdit ? (
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => setModal({ mode: 'create', labels: [] })}>新建标签</Button>
        ) : null}
        {canPrint ? (
          <Tooltip title={selectedLabels.length > printableSelected.length ? `选中的标签里有 ${selectedLabels.length - printableSelected.length} 个已消耗，不会打印` : ''}>
            <Button disabled={printableSelected.length === 0} onClick={() => setModal({ mode: 'print', labels: printableSelected })}>
              批量打印选中的标签{printableSelected.length > 0 ? `（${printableSelected.length}）` : ''}
            </Button>
          </Tooltip>
        ) : null}
        {canEdit ? (
          <Button
            disabled={selectedLabels.length === 0}
            onClick={() => { setGroupTarget({ ids: selectedLabels.map(label => label.id), group: '' }); setGroupDraft(''); }}
          >
            修改选中的标识组
          </Button>
        ) : null}
        {canConsume ? (
          <Button disabled={!selectedLabels.some(canConsumeRow)} onClick={() => askConsume(selectedLabels.filter(canConsumeRow).map(label => label.id), true, `选中的 ${selectedLabels.filter(canConsumeRow).length} 个容器`)}>
            选中的标记已消耗
          </Button>
        ) : null}
        {canConsume ? (
          <Button disabled={!selectedLabels.some(label => label.consumed)} onClick={() => askConsume(selectedLabels.filter(label => label.consumed).map(label => label.id), false, '')}>
            选中的取消已消耗
          </Button>
        ) : null}
        {canPrint && unprinted > 0 ? <Tag color="orange">{unprinted} 张还没有打印</Tag> : null}
        {consumedTotal > 0 ? <Tag>已消耗 {consumedTotal} 个</Tag> : null}
        <Tooltip title={HINT}><QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} /></Tooltip>
      </Space>
      {labels.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="还没有标签" />
      ) : (
        <LabelGroupSections rows={labels} renderTable={renderTable} renderExtra={renderSectionActions} />
      )}
      <LabelPrintModal
        open={!!modal}
        instructionId={instructionId}
        instructionCode={instructionCode}
        mode={modal?.mode || 'create'}
        labels={modal?.labels}
        nextNumber={nextNumber}
        groups={groups}
        lastGroup={lastGroup}
        allLabels={labels}
        onClose={() => setModal(null)}
        onChanged={onChanged}
      />
      <Modal
        title="修改标识组"
        open={!!groupTarget}
        okText="保存"
        cancelText="取消"
        confirmLoading={busy}
        onOk={submitGroup}
        onCancel={() => setGroupTarget(null)}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          把 {groupTarget?.ids.length || 0} 个标签改到下面的标识组（选已有的组，或直接输入新的组名；留空 = 移出分组）：
        </div>
        <AutoComplete
          value={groupDraft}
          style={{ width: '100%' }}
          allowClear
          placeholder="选择已有的组，或输入新的组名"
          options={(groups || []).map(item => ({ value: item.name }))}
          filterOption={groupFilterOption((groups || []).map(item => item.name))}
          onChange={(value: string) => setGroupDraft(String(value ?? '').slice(0, 30))}
        >
          <Input maxLength={30} />
        </AutoComplete>
        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>只改分组，不改编号；已经打印出来的实物标签上的分组不会变，需要的话重新打印。</div>
      </Modal>
    </div>
  );
};

export default LabelsPanel;
